import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Sparkle,
  X,
  WarningCircle,
  CheckCircle,
  CalendarBlank,
  Briefcase,
  Flag,
  PaperPlaneRight,
  PencilSimple,
  ArrowRight,
  Lightning
} from '@phosphor-icons/react';

const API = `${window.location.origin}/api`;

const CONFIDENCE_STYLES = {
  high: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: CheckCircle },
  medium: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: WarningCircle },
  low: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: WarningCircle },
};

function AITaskPreview({ parsed, originalText, suggestedTemplates, mode, taskId, onSave, onCancel, onUseTemplate, projects }) {
  const [fields, setFields] = useState({
    title: parsed.title || '',
    due_date: parsed.due_date || '',
    project_client: parsed.project_client || '',
    priority: parsed.priority || 'medium',
    status: parsed.status || '',
    notes: parsed.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [showProjectSuggestions, setShowProjectSuggestions] = useState(false);

  const confidence = parsed.confidence || 'medium';
  const confStyle = CONFIDENCE_STYLES[confidence];
  const ConfIcon = confStyle.icon;

  const filteredProjects = (projects || []).filter(
    p => p.toLowerCase().includes(fields.project_client.toLowerCase()) && p !== fields.project_client
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      if (mode === 'new_task') {
        const taskData = {
          title: fields.title,
          priority: fields.priority,
          due_date: fields.due_date || undefined,
          project_client: fields.project_client || undefined,
          description: fields.notes || undefined,
          tags: [],
        };
        const res = await axios.post(`${API}/tasks`, taskData, { withCredentials: true });
        if (onSave) onSave(res.data);
      } else if (mode === 'update_task' && taskId) {
        const body = {
          note: fields.notes || fields.title,
        };
        if (fields.status) body.status = fields.status;
        if (fields.project_client) body.project_client = fields.project_client;
        if (fields.notes) body.details = fields.notes;
        await axios.post(`${API}/tasks/${taskId}/quick-update`, body, { withCredentials: true });

        // Also update due_date if parsed
        if (fields.due_date) {
          await axios.put(`${API}/tasks/${taskId}`, { due_date: fields.due_date }, { withCredentials: true });
        }
        if (onSave) onSave();
      }
    } catch (error) {
      console.error('Save failed:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden"
      data-testid="ai-task-preview"
    >
      {/* Header */}
      <div className="bg-navy text-white px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkle size={16} weight="fill" className="text-blue-400" />
          <span className="text-sm font-medium">
            {mode === 'new_task' ? 'AI Parsed — New Task' : 'AI Parsed — Task Update'}
          </span>
        </div>
        <button onClick={onCancel} className="text-slate-400 hover:text-white transition-colors" data-testid="cancel-preview">
          <X size={18} />
        </button>
      </div>

      {/* Confidence indicator */}
      {(confidence !== 'high' || parsed.confidence_note) && (
        <div className={`${confStyle.bg} ${confStyle.border} border-b px-5 py-2.5 flex items-center gap-2`}>
          <ConfIcon size={16} weight="fill" className={confStyle.text} />
          <p className={`text-xs ${confStyle.text}`}>
            {parsed.confidence_note || `${confidence} confidence — please review the parsed fields.`}
          </p>
        </div>
      )}

      {/* Original input */}
      <div className="px-5 pt-4 pb-2">
        <p className="text-xs text-slate-400 mb-1">You said:</p>
        <p className="text-sm text-slate-600 italic">"{originalText}"</p>
      </div>

      {/* Editable fields */}
      <div className="px-5 py-3 space-y-3">
        {/* Title */}
        <div>
          <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1">
            <PencilSimple size={12} /> Title
          </label>
          <input
            type="text"
            value={fields.title}
            onChange={(e) => setFields({ ...fields, title: e.target.value })}
            className="bg-white border border-slate-200 rounded-lg px-3 py-2 w-full text-sm text-slate-900 focus:border-slate-900 focus:ring-1 focus:ring-slate-100"
            data-testid="preview-title"
          />
        </div>

        {/* Due date + Priority row */}
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1">
              <CalendarBlank size={12} /> Due Date
            </label>
            <input
              type="date"
              value={fields.due_date}
              onChange={(e) => setFields({ ...fields, due_date: e.target.value })}
              className="bg-white border border-slate-200 rounded-lg px-3 py-2 w-full text-sm text-slate-700"
              data-testid="preview-due-date"
            />
          </div>
          <div className="flex-1">
            <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1">
              <Flag size={12} /> Priority
            </label>
            <select
              value={fields.priority}
              onChange={(e) => setFields({ ...fields, priority: e.target.value })}
              className="bg-white border border-slate-200 rounded-lg px-3 py-2 w-full text-sm text-slate-700"
              data-testid="preview-priority"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
        </div>

        {/* Project + Status row */}
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1">
              <Briefcase size={12} /> Project / Client
            </label>
            <input
              type="text"
              value={fields.project_client}
              onChange={(e) => { setFields({ ...fields, project_client: e.target.value }); setShowProjectSuggestions(true); }}
              onFocus={() => setShowProjectSuggestions(true)}
              onBlur={() => setTimeout(() => setShowProjectSuggestions(false), 150)}
              placeholder="e.g., Zenith Bank"
              className="bg-white border border-slate-200 rounded-lg px-3 py-2 w-full text-sm text-slate-700"
              data-testid="preview-project"
            />
            {showProjectSuggestions && filteredProjects.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-md z-10 max-h-28 overflow-y-auto">
                {filteredProjects.map(p => (
                  <button key={p} type="button" className="block w-full text-left px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
                    onMouseDown={() => { setFields({ ...fields, project_client: p }); setShowProjectSuggestions(false); }}
                  >{p}</button>
                ))}
              </div>
            )}
          </div>
          {mode === 'update_task' && (
            <div className="flex-1">
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1">
                <ArrowRight size={12} /> Status
              </label>
              <select
                value={fields.status}
                onChange={(e) => setFields({ ...fields, status: e.target.value })}
                className="bg-white border border-slate-200 rounded-lg px-3 py-2 w-full text-sm text-slate-700"
                data-testid="preview-status"
              >
                <option value="">No change</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Notes / Details</label>
          <textarea
            value={fields.notes}
            onChange={(e) => setFields({ ...fields, notes: e.target.value })}
            placeholder="Additional context..."
            className="bg-white border border-slate-200 rounded-lg px-3 py-2 w-full text-sm text-slate-700 min-h-[50px] resize-none"
            data-testid="preview-notes"
          />
        </div>
      </div>

      {/* Suggested Workflow Templates */}
      {suggestedTemplates && suggestedTemplates.length > 0 && onUseTemplate && (
        <div className="px-5 pb-3">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs font-medium text-blue-700 mb-2 flex items-center gap-1">
              <Lightning size={12} weight="fill" />
              Matching workflow template{suggestedTemplates.length > 1 ? 's' : ''} found:
            </p>
            <div className="space-y-1.5">
              {suggestedTemplates.map((tmpl) => (
                <button
                  key={tmpl.wf_template_id}
                  onClick={() => onUseTemplate(tmpl.wf_template_id)}
                  className="w-full text-left bg-white hover:bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-sm transition-colors flex items-center justify-between"
                  data-testid={`ai-suggest-template-${tmpl.wf_template_id}`}
                >
                  <span className="font-medium text-slate-800">{tmpl.name}</span>
                  <span className="text-xs text-navy font-medium flex items-center gap-1">
                    Use template <ArrowRight size={10} />
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="px-5 pb-4 flex gap-2">
        <button
          onClick={handleSave}
          disabled={saving || !fields.title.trim()}
          className="flex-1 bg-navy text-white hover:bg-navy-hover transition-colors font-medium py-2.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
          data-testid="confirm-save-btn"
        >
          <PaperPlaneRight size={14} weight="fill" />
          {saving ? 'Saving...' : mode === 'new_task' ? 'Create Task' : 'Save Update'}
        </button>
        <button
          onClick={onCancel}
          className="bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors font-medium px-4 py-2.5 rounded-lg text-sm"
          data-testid="cancel-save-btn"
        >
          Cancel
        </button>
      </div>
    </motion.div>
  );
}

export default AITaskPreview;
